import {announce} from '@github-ui/aria-live'
import {type BoardCell, boardCellKey, type BoardPaging, type CellAddress} from '@github-ui/board-core'
import {hashKey, matchMutation, type QueryKey, useQueryClient} from '@github-ui/react-query'
import {useEffect, useMemo, useRef, useState} from 'react'

import {appendCellPage} from './append-cell-page'
import {cellPageCursor, cellsToPreload} from './cell-page-eligibility'
import {replaceCellPage, withHeldCopies} from './replace-cell-page'
import type {
  CellPageOutcome,
  SavedViewsBoardAdapter,
  SavedViewsBoardCellPage,
  SavedViewsBoardData,
  SavedViewsBoardItem,
  SavedViewsBoardOptions,
  SavedViewsBoardPayload,
} from './types'
import {BOARD_MOVES} from './use-board-item-move'

const NO_CELLS: ReadonlySet<string> = new Set()

// The cell announced that it was loading, so a skeleton that simply disappears leaves a reader
// who cannot see it waiting on a page that is never coming.
const PAGE_FAILED = 'Could not load more cards. Try again.'
const REFRESH_FAILED = 'Could not reload these cards.'

/**
 * Columns and rows past the window to read first pages for; the window itself already reaches one
 * past the screen. Measured on a Planning board: a page takes 240 to 330ms, and a fast sideways
 * scroll (~3,400px/s) outran one column of lead but not three. Scrolling down kept up with one row.
 */
const CELL_PRELOAD_LEAD = {columns: 3, rows: 1}

/**
 * Preloads use only spare capacity: one starts while fewer cell requests than this are out, counting
 * the ones the reader is waiting on, and a reader's own request is never held back by it. Four
 * filled up on a fast sideways scroll across a single lane.
 */
const MAX_CELL_REQUESTS_FOR_PRELOAD = 6

function findCell<TItem>(cells: ReadonlyArray<BoardCell<TItem>>, cellKey: string) {
  return cells.find(candidate => boardCellKey(candidate) === cellKey)
}

/**
 * A request is current only while both its board and request IDs match: a board can be left and
 * returned to, and one cell can be asked twice from the same cursor after a failure. Only the
 * current request may append or clear the cell's loading state.
 */
type CellRequest<TItem extends SavedViewsBoardItem> = {
  boardId: string
  requestId: number
  controller: AbortController
  /** The newest board move when it was sent. A move made while it was out has a later id. */
  movesSeen: number
  /** Null for a first page. */
  cursor: string | null
  /** Asked for ahead of the reader rather than by them. Stays true once a reader adopts it. */
  preload: boolean
  /** A reader is waiting on it, so it lights the cell's loading state and reports a failure. */
  demanded: boolean
  /** Set when it reloads the cell, so an interrupted reload is sent again as one. */
  refresh?: CellRefresh<TItem>
}

type BoardCellPaging<TItem extends SavedViewsBoardItem> = {
  /** Undefined where the domain cannot page a cell, which leaves its sentinels unobserved. */
  onLoadMoreItems?: (address: CellAddress) => void
  /**
   * Replaces one cell's cards with its first page, read again. A page in flight for the cell is
   * dropped, since it continues from the cards being replaced. `reconcile` is handed the page's
   * cards and the cards the cell holds when the page lands, and returns the cards to draw. Waits
   * for a pending board move, like any other cell page. Resolves with how the reload ended, and
   * never rejects. Undefined where the domain cannot page a cell.
   */
  refreshCell?: (address: CellAddress, reconcile?: ReconcileCellPage<TItem>) => Promise<CellPageOutcome>
  /** Undefined where the domain cannot page a cell, so nothing is read ahead. */
  onVisibleKeysChange?: BoardPaging['onVisibleKeysChange']
  /** Undefined where the domain cannot page a cell, so a cell's next page waits for its sentinel. */
  onApproachItems?: BoardPaging['onApproachItems']
  /** Keyed by board-core cell key. */
  loadingCells: ReadonlySet<string>
  invalidatePages: () => void
}

type ReconcileCellPage<TItem extends SavedViewsBoardItem> = (
  page: readonly TItem[],
  held: readonly TItem[],
) => readonly TItem[]

/** A cell to read again, and how to say the reload has ended. */
type CellRefresh<TItem extends SavedViewsBoardItem> = {
  address: CellAddress
  reconcile?: ReconcileCellPage<TItem>
  settle: (outcome: CellPageOutcome) => void
}

/**
 * Asks for one cell's next page and folds it into the board already on screen. Reads ahead of the
 * reader in two ways: the first page of unloaded cells near the renderer's window, and the next
 * page of a cell the reader is scrolling towards the end of.
 *
 * Loading is held here rather than in the cache, which would hand a stuck spinner to the next
 * reader of the same board. Only a page the reader asked for lights it or reports a failure; a
 * preload is silent. A reader's page that failed is not retried: the cell is left as it was, so
 * board-core's sentinel asks again on the reader's next settle. A cursor that failed is not read
 * ahead again, though the reader can still ask for it.
 *
 * A board move outdates every page read before it settles, and these pages are fetched outside the
 * query, so the settlement's own cancellation never reaches them. No page is read while a move is
 * pending, and a page that was out while one was made is dropped and read again afterwards. That
 * rejects a page older than the move; it cannot make one read after the move fresher than the
 * search index (github/views#105).
 *
 * Two windows can still disagree — a relative date crossing its boundary between requests, and
 * a saved view edited while its board is on screen. Both want the effective query bound the way
 * the axes already are (github/views#177 follow-up).
 */
export function useBoardCellPaging<TItem extends SavedViewsBoardItem>({
  adapter,
  options,
  queryKey,
  drawnBoard,
}: {
  adapter: Pick<SavedViewsBoardAdapter<TItem>, 'fetchCellPage'>
  options: SavedViewsBoardOptions
  queryKey: QueryKey
  /** The board as last drawn, which is what a sentinel's ask is about. Undefined before it is. */
  drawnBoard: SavedViewsBoardData<TItem> | undefined
}): BoardCellPaging<TItem> {
  const queryClient = useQueryClient()
  const [loadingCells, setLoadingCells] = useState<ReadonlySet<string>>(NO_CELLS)
  const requestsRef = useRef(new Map<string, CellRequest<TItem>>())
  // The cursor each cell's last failed page was read from, null for a first page.
  const spentRef = useRef(new Map<string, string | null>())
  // Readers who asked while a board move was pending, answered once it settles.
  const waitingRef = useRef(new Map<string, CellAddress>())
  // Cells to read again once the pending board move settles.
  const refreshesRef = useRef(new Map<string, CellRefresh<TItem>>())
  const windowRef = useRef<{columnKeys?: readonly string[]; rowKeys?: readonly string[]}>({})
  const frameRef = useRef(0)
  const drawnRef = useRef(drawnBoard)
  const lastRequestIdRef = useRef(0)

  const boardId = hashKey(queryKey)
  const {fetchCellPage} = adapter
  const {query} = options

  useEffect(() => {
    drawnRef.current = drawnBoard
  }, [drawnBoard])

  // Abandoning a board abandons its requests: their answers describe a grid that is no longer
  // drawn, and a cell left recorded as loading would still be loading when the board returns. The
  // window is kept, since it describes the renderer rather than the board.
  useEffect(() => {
    const outstanding = requestsRef.current
    const spent = spentRef.current
    const waiting = waitingRef.current
    const refreshes = refreshesRef.current
    return () => {
      for (const request of outstanding.values()) request.controller.abort()
      outstanding.clear()
      spent.clear()
      waiting.clear()
      for (const refresh of refreshes.values()) refresh.settle('abandoned')
      refreshes.clear()
      setLoadingCells(NO_CELLS)
    }
  }, [boardId])

  const paging = useMemo(() => {
    const markLoading = (cellKey: string) =>
      setLoadingCells(previous => (previous.has(cellKey) ? previous : new Set(previous).add(cellKey)))

    const clearLoading = (cellKey: string) =>
      setLoadingCells(previous => {
        if (!previous.has(cellKey)) return previous
        const remaining = new Set(previous)
        remaining.delete(cellKey)
        return remaining
      })

    const held = () => queryClient.getQueryData<SavedViewsBoardPayload<TItem, unknown>>(queryKey)

    const isMovePending = () => queryClient.isMutating(BOARD_MOVES) > 0

    // Ids rather than `submittedAt`: a move and a request started in the same millisecond have
    // the same timestamp, and the cache numbers its mutations in the order it made them.
    const boardMoves = () => queryClient.getMutationCache().findAll(BOARD_MOVES)
    const newestMove = () => Math.max(0, ...boardMoves().map(move => move.mutationId))
    const movedSince = (movesSeen: number) =>
      boardMoves().some(move => move.state.status === 'pending' || move.mutationId > movesSeen)

    const isCurrent = (cellKey: string, request: CellRequest<TItem>) => {
      const current = requestsRef.current.get(cellKey)
      return current?.boardId === request.boardId && current.requestId === request.requestId
    }

    // Whether a reload is out or waiting on a move, under any request.
    const isQueued = (cellKey: string, refresh: CellRefresh<TItem>) =>
      requestsRef.current.get(cellKey)?.refresh === refresh || refreshesRef.current.get(cellKey) === refresh

    const release = (cellKey: string, request: CellRequest<TItem>) => {
      requestsRef.current.delete(cellKey)
      if (request.demanded) clearLoading(cellKey)
    }

    // Guarded on what the cache holds when the page lands, not on what was eligible when it was
    // asked for: a page can arrive after another one, or an axis page, already filled the cell.
    // Answers how many cards the cell gained, or null when nothing was written.
    const write = (
      address: CellAddress,
      cellKey: string,
      cursor: string | null,
      page: SavedViewsBoardCellPage<TItem>,
    ) => {
      let added: number | null = null
      queryClient.setQueryData<SavedViewsBoardPayload<TItem, unknown>>(queryKey, currentData => {
        if (!currentData) return undefined
        // Request identity cannot say the board under the key is still the one the cursor was read
        // from. Nothing replaces a board in place today, so this supplements that check.
        const currentCell = findCell(currentData.boardData.data.cells, cellKey)
        const firstPageAlreadyLoaded = cursor === null && currentCell?.itemsLoaded !== false
        const cursorAdvanced = cursor !== null && currentData.boardData.cellCursors?.get(cellKey) !== cursor
        if (firstPageAlreadyLoaded || cursorAdvanced) return undefined

        const nextData = appendCellPage(currentData, address, page)
        // `undefined` is the only answer the cache treats as no write, and `appendCellPage`
        // answers by reference only when the cell is no longer drawn. A page that added no
        // card still writes, since it can advance that cell's cursor or finish it.
        if (nextData === currentData) return undefined

        const nextCell = findCell(nextData.boardData.data.cells, cellKey)
        added = (nextCell?.items.length ?? 0) - (currentCell?.items.length ?? 0)
        return nextData
      })
      return added
    }

    // Declared after `send`, which calls them, and the compiler cannot check a memo's dependencies
    // across a forward reference.
    const after: {
      demand: (address: CellAddress) => void
      queueRefresh: (refresh: CellRefresh<TItem>) => void
      readAhead: (address: CellAddress, cellKey: string, cursor: string) => void
      plan: () => void
    } = {demand: () => {}, queueRefresh: () => {}, readAhead: () => {}, plan: () => {}}

    const send = (
      address: CellAddress,
      cellKey: string,
      cursor: string | null,
      {preload, demanded}: {preload: boolean; demanded: boolean},
    ) => {
      if (!fetchCellPage) return

      const request: CellRequest<TItem> = {
        boardId,
        requestId: (lastRequestIdRef.current += 1),
        controller: new AbortController(),
        movesSeen: newestMove(),
        cursor,
        preload,
        demanded,
      }
      requestsRef.current.set(cellKey, request)
      if (demanded) markLoading(cellKey)

      // Settled after the try rather than in a `finally`, which React Compiler cannot lower.
      const load = async () => {
        let page: SavedViewsBoardCellPage<TItem> | undefined
        try {
          page = await fetchCellPage({...address, cursor, query, signal: request.controller.signal})
        } catch {
          page = undefined
        }
        // An abort clears the record too, so this also covers a board that was left.
        if (!isCurrent(cellKey, request)) return

        release(cellKey, request)

        if (page === undefined) {
          // Whoever asked, a cursor that failed is left to the sentinel.
          spentRef.current.set(cellKey, cursor)
          if (request.demanded) announce(PAGE_FAILED)
        } else if (movedSince(request.movesSeen)) {
          if (request.demanded) after.demand(address)
        } else {
          const added = write(address, cellKey, cursor, page)
          // A page of cards the cell already held leaves it deep-equal, so the cache keeps the old
          // cell and board-core never reports again. Go on from here until a page brings a card.
          const nextCursor = page.itemsCursor
          const wasReadAhead = request.preload && !request.demanded && cursor !== null
          if (added === 0 && wasReadAhead && nextCursor != null && nextCursor !== cursor) {
            after.readAhead(address, cellKey, nextCursor)
          }
        }

        after.plan()
      }

      void load()
    }

    // A cell's first page in place of its cards, which a reader is waiting on like a page they asked for.
    const sendRefresh = (cellKey: string, refresh: CellRefresh<TItem>) => {
      const {address, reconcile, settle} = refresh
      if (!fetchCellPage) {
        settle('failed')
        return
      }

      const request: CellRequest<TItem> = {
        boardId,
        requestId: (lastRequestIdRef.current += 1),
        controller: new AbortController(),
        movesSeen: newestMove(),
        cursor: null,
        preload: false,
        demanded: true,
        refresh,
      }
      requestsRef.current.set(cellKey, request)
      markLoading(cellKey)

      // Settled after the try rather than in a `finally`, which React Compiler cannot lower.
      const load = async () => {
        let page: SavedViewsBoardCellPage<TItem> | undefined
        try {
          page = await fetchCellPage({...address, cursor: null, query, signal: request.controller.signal})
        } catch {
          page = undefined
        }
        // An abort clears the record too, so this also covers a board that was left. A reload
        // invalidatePages sent again settles with the newer request.
        if (!isCurrent(cellKey, request)) {
          if (!isQueued(cellKey, refresh)) settle('abandoned')
          return
        }

        release(cellKey, request)

        if (page === undefined) {
          // A failed reload leaves the cell as it was, and its first page to the reader.
          spentRef.current.set(cellKey, null)
          announce(REFRESH_FAILED)
          settle('failed')
        } else if (movedSince(request.movesSeen)) {
          after.queueRefresh(refresh)
        } else {
          // React Compiler fails MemoDependencies on `paging` when the updater captures the reassigned `page`.
          const firstPage = page
          queryClient.setQueryData<SavedViewsBoardPayload<TItem, unknown>>(queryKey, currentData => {
            if (!currentData) return undefined
            const cards = findCell(currentData.boardData.data.cells, cellKey)?.items ?? []
            const kept = withHeldCopies(firstPage.items, cards)
            const items = reconcile ? reconcile(kept, cards) : kept
            const nextData = replaceCellPage(currentData, address, {...firstPage, items})
            return nextData === currentData ? undefined : nextData
          })
          settle('drawn')
        }

        after.plan()
      }

      void load()
    }

    const queueRefresh = (refresh: CellRefresh<TItem>) => {
      const cellKey = boardCellKey(refresh.address)
      // A page in flight continues from the cards being replaced, and a reader waiting on the
      // cell is answered by the first page.
      requestsRef.current.get(cellKey)?.controller.abort()
      requestsRef.current.delete(cellKey)
      waitingRef.current.delete(cellKey)
      const replaced = refreshesRef.current.get(cellKey)
      if (replaced !== refresh) replaced?.settle('abandoned')

      if (isMovePending()) {
        refreshesRef.current.set(cellKey, refresh)
        markLoading(cellKey)
        return
      }

      refreshesRef.current.delete(cellKey)
      sendRefresh(cellKey, refresh)
    }

    const refresh = (address: CellAddress, reconcile?: ReconcileCellPage<TItem>) => {
      const {promise, resolve} = Promise.withResolvers<CellPageOutcome>()
      queueRefresh({address, reconcile, settle: resolve})
      return promise
    }

    const isSpent = (cellKey: string, cursor: string | null) => spentRef.current.get(cellKey) === cursor

    // The page a reader at the end of the drawn cell is asking for. Null when there is none, or when
    // a page has landed since the board drew the cell, since that page is the one they reached for.
    const pageToAsk = (cellKey: string) => {
      const payload = held()
      const next = cellPageCursor(
        payload && findCell(payload.boardData.data.cells, cellKey),
        payload?.boardData.cellCursors,
      )
      const drawn = drawnRef.current
      const drawnCell = drawn && findCell(drawn.data.cells, cellKey)
      if (drawnCell !== undefined && cellPageCursor(drawnCell, drawn?.cellCursors)?.cursor !== next?.cursor) return null

      return next
    }

    const demand = (address: CellAddress) => {
      const cellKey = boardCellKey(address)
      const outstanding = requestsRef.current.get(cellKey)
      // board-core holds a second ask while the cell states it is loading, but this runs before
      // that state has rendered, so this covers the gap and a host that never threads loading.
      // A reload queued behind a move answers the reader with the first page.
      if (outstanding?.demanded || waitingRef.current.has(cellKey) || refreshesRef.current.has(cellKey)) return

      const next = pageToAsk(cellKey)
      if (next === null) return

      if (outstanding) {
        // A preload for this page is adopted, so the reader waits on it rather than on a second request.
        if (outstanding.cursor === next.cursor) {
          outstanding.demanded = true
          markLoading(cellKey)
          return
        }

        // It asked for a page that an axis page or a refetch has brought since.
        outstanding.controller.abort()
        requestsRef.current.delete(cellKey)
      }

      if (isMovePending()) {
        waitingRef.current.set(cellKey, address)
        markLoading(cellKey)
        return
      }

      send(address, cellKey, next.cursor, {preload: false, demanded: true})
    }

    // Under the same limits as reading a first page ahead.
    const readAhead = (address: CellAddress, cellKey: string, cursor: string) => {
      if (requestsRef.current.has(cellKey) || waitingRef.current.has(cellKey)) return
      if (requestsRef.current.size >= MAX_CELL_REQUESTS_FOR_PRELOAD || isMovePending()) return
      if (isSpent(cellKey, cursor)) return

      send(address, cellKey, cursor, {preload: true, demanded: false})
    }

    // A next page only: an unloaded cell's first page is the window's to read.
    const approach = (address: CellAddress) => {
      const cellKey = boardCellKey(address)
      const next = pageToAsk(cellKey)
      if (next?.cursor == null) return

      readAhead(address, cellKey, next.cursor)
    }

    const plan = () => {
      if (!fetchCellPage || isMovePending()) return

      const {columnKeys, rowKeys} = windowRef.current
      const payload = held()
      if (columnKeys === undefined || rowKeys === undefined || payload === undefined) return

      // Recomputed from the cache and the window each time, so a cell the reader has moved away
      // from is simply never started. One already out is left to land: aborting it saves the
      // server nothing, and the slot it freed would only start another.
      for (const address of cellsToPreload(payload.boardData.data, {columnKeys, rowKeys}, CELL_PRELOAD_LEAD)) {
        if (requestsRef.current.size >= MAX_CELL_REQUESTS_FOR_PRELOAD) return

        const cellKey = boardCellKey(address)
        if (requestsRef.current.has(cellKey) || waitingRef.current.has(cellKey) || isSpent(cellKey, null)) {
          continue
        }
        send(address, cellKey, null, {preload: true, demanded: false})
      }
    }

    after.demand = demand
    after.queueRefresh = queueRefresh
    after.readAhead = readAhead
    after.plan = plan

    // One pass per frame, so the two axes reporting the same scroll, and the unmeasured window
    // that a measured one replaces before paint, cost one plan.
    const schedulePlan = () => {
      cancelAnimationFrame(frameRef.current)
      frameRef.current = requestAnimationFrame(plan)
    }

    const resume = () => {
      if (isMovePending()) return

      for (const [cellKey, queued] of [...refreshesRef.current]) {
        refreshesRef.current.delete(cellKey)
        sendRefresh(cellKey, queued)
      }
      for (const [cellKey, address] of [...waitingRef.current]) {
        waitingRef.current.delete(cellKey)
        clearLoading(cellKey)
        demand(address)
      }
      schedulePlan()
    }

    // An axis reports the lanes it is read against, so the column axis reports the rows drawn and
    // the row axis the columns.
    const onVisibleKeysChange = (axis: 'columns' | 'rows', visibleKeys: readonly string[]) => {
      if (axis === 'columns') windowRef.current.rowKeys = visibleKeys
      else windowRef.current.columnKeys = visibleKeys
      schedulePlan()
    }

    const invalidatePages = () => {
      const waiting: CellAddress[] = []
      const reloads: Array<CellRefresh<TItem>> = []
      for (const [cellKey, request] of requestsRef.current) {
        request.controller.abort()
        // A reload asks for the first page, and the cell's cursor leads past it.
        if (request.refresh) reloads.push(request.refresh)
        else if (request.demanded) {
          clearLoading(cellKey)
          const cell = held()?.boardData.data.cells.find(candidate => boardCellKey(candidate) === cellKey)
          if (cell) waiting.push({columnKey: cell.columnKey, rowKey: cell.rowKey})
        }
      }
      requestsRef.current.clear()
      spentRef.current.clear()
      for (const reload of reloads) queueRefresh(reload)
      for (const address of waiting) demand(address)
      schedulePlan()
    }

    return {demand, refresh, approach, onVisibleKeysChange, resume, schedulePlan, invalidatePages}
  }, [boardId, fetchCellPage, query, queryClient, queryKey])

  // Plans each new board's first preloads. Moves are heard from the mutation cache rather than
  // through a render, which a move that starts and settles between two renders would never reach.
  useEffect(() => {
    paging.resume()
    const unsubscribe = queryClient.getMutationCache().subscribe(event => {
      if (event.type !== 'updated' || event.mutation.state.status === 'pending') return
      if (matchMutation(BOARD_MOVES, event.mutation)) paging.resume()
    })

    return () => {
      unsubscribe()
      cancelAnimationFrame(frameRef.current)
    }
  }, [paging, queryClient])

  // An axis page can bring unloaded cells near a window that has not moved.
  useEffect(() => {
    paging.schedulePlan()
  }, [paging, drawnBoard])

  return {
    onLoadMoreItems: fetchCellPage ? paging.demand : undefined,
    refreshCell: fetchCellPage ? paging.refresh : undefined,
    onVisibleKeysChange: fetchCellPage ? paging.onVisibleKeysChange : undefined,
    onApproachItems: fetchCellPage ? paging.approach : undefined,
    loadingCells,
    invalidatePages: paging.invalidatePages,
  }
}

/** The board as drawn, with the cells that have a request outstanding reporting it. */
export function withLoadingCells<TItem extends SavedViewsBoardItem>(
  boardData: SavedViewsBoardData<TItem>,
  loadingCells: ReadonlySet<string>,
): SavedViewsBoardData<TItem> {
  if (loadingCells.size === 0) return boardData

  return {
    ...boardData,
    data: {
      ...boardData.data,
      cells: boardData.data.cells.map(cell =>
        loadingCells.has(boardCellKey(cell)) ? {...cell, isLoadingItems: true} : cell,
      ),
    },
  }
}
